/* eslint-disable react-hooks/exhaustive-deps */
import { AxiosResponse } from 'axios';
import { Divider } from 'primereact/divider';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import LedgerFormulaTooltip from '../../components/ledger-formula-tooltip';
import Loader from '../../components/loader';
import { APIResponseEntity } from '../../interface/api-responce';
import { ViewLedgerDetailsRespEntity } from '../../interface/ledger-form';
import { APIGetLedgerDetailsByOrderId } from '../../service/api-service';
import { displayProjectClosingBalance } from '../../service/shared.service';
import { toastOptObj } from '../../utils/constants';
import { DelivaryAddressStatusEnum, OfficeTypeEnum, OrderStatusEnumEntity } from '../../utils/enum';
import { IsNullOrUndefined, IsStringNullEmptyOrUndefined } from '../../utils/null-check';
import { resolveTooltipAccessMap, TooltipAccessConfig } from '../../utils/tooltip-access';

type LedgerTooltipKey = 'due' | 'overdue' | 'dealerPrice' | 'webappSales' | 'projectClosingBalance' | 'netSales' | 'netCollection';

const LEDGER_TOOLTIP_ACCESS_CONFIG: TooltipAccessConfig<LedgerTooltipKey> = {
    due: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
    overdue: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
    dealerPrice: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
    webappSales: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
    netSales: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
    netCollection: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
    projectClosingBalance: { requires: { action: 'upgrade', module: 'Access to view ledger detail tooltip' } },
};

export default function ViewLedgerDetails({ modelOrderId, ledgerDetailsCB }: { modelOrderId: string; ledgerDetailsCB: (data: ViewLedgerDetailsRespEntity | null) => void }) {

    const [ledgerDetails, setLedgerDetails] = useState<ViewLedgerDetailsRespEntity | null>(null)

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [orderId, setOrderId] = useState<string>('');
    const [activeTooltip, setActiveTooltip] = useState<LedgerTooltipKey | null>(null);
    const tooltipAccessMap = useMemo(
        () => resolveTooltipAccessMap(LEDGER_TOOLTIP_ACCESS_CONFIG),
        []
    );

    const param = useParams();

    useEffect(() => {
        const fetchDefaultData = async (): Promise<void> => {
            setLedgerDetails(null);
            setOrderId('');
            if (param && param.id && !IsStringNullEmptyOrUndefined(param.id)) {
                setOrderId(param.id);
            } else {
                if (modelOrderId && !IsStringNullEmptyOrUndefined(modelOrderId)) {
                    setOrderId(modelOrderId);
                }
            }
        }
        fetchDefaultData();
    }, [param, modelOrderId]);

    useEffect(() => {
        const fetchDefaultData = async (): Promise<void> => {
            setLedgerDetails(null);
            if (orderId && !IsStringNullEmptyOrUndefined(orderId)) {
                await fetchLedgerDetailsByOrderId();
            }
        }
        fetchDefaultData();
    }, [orderId]);

    const fetchLedgerDetailsByOrderId = async () => {
        setIsLoading(true);
        const resp: AxiosResponse<APIResponseEntity> = await APIGetLedgerDetailsByOrderId(orderId);
        setIsLoading(false);

        if (resp) {
            if (resp.data && resp.status === 200) {
                const axiosResp = resp.data;
                if (axiosResp && axiosResp.data && axiosResp.status &&
                    axiosResp.statusCode === 200) {
                    setLedgerDetails(axiosResp.data);
                    ledgerDetailsCB(axiosResp.data);
                } else {
                    toast.error(axiosResp.message, { ...toastOptObj });
                }
            }
            else {
                toast.error(resp.data.message, { ...toastOptObj });
            }
        }
    };

    const statusBodyTemplate = (orderStatus: string): string => {
        const status =
            orderStatus === OrderStatusEnumEntity.DRAFT
                ? "Draft"
                : orderStatus === OrderStatusEnumEntity.PIPELINE
                    ? "Pipeline"
                    : orderStatus === OrderStatusEnumEntity.COMMERCIALHOLD
                        ? "Commercial Hold"
                        : orderStatus === OrderStatusEnumEntity.COMMERCIALWIN
                            ? "Win"
                            : orderStatus === OrderStatusEnumEntity.FEEDBACK
                                ? "Feedback"
                                : orderStatus === OrderStatusEnumEntity.FULL_DISPATCH
                                    ? "Full Dispatch"
                                    : orderStatus === OrderStatusEnumEntity.OLDWIN
                                        ? "Old Win"
                                        : orderStatus === OrderStatusEnumEntity.HANDOVER
                                            ? "Handover"
                                            : orderStatus === OrderStatusEnumEntity.HANDOVER_PENDING
                                                ? "Handover Pending"
                                                : orderStatus === OrderStatusEnumEntity.EDITED
                                                    ? "Edited"
                                                    : orderStatus === OrderStatusEnumEntity.EXPIRED
                                                        ? "Expired"
                                                        : orderStatus === OrderStatusEnumEntity.LOST
                                                            ? "Lost"
                                                            : orderStatus === OrderStatusEnumEntity.PRECLOSURE
                                                                ? "Preclosure"
                                                                : "";

        return status;
    };

    const parseAmount = (value: string | number | undefined | null): number => {
        if (value === null || value === undefined) return 0;
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        const sanitized = value.replace(/,/g, '').trim();
        const parsed = Number(sanitized);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const formatAmount = (value: number): string => {
        return `${Math.round(value)}`;
    };

    const roundTo2 = (value: number): number => {
        return Math.round(value);
    };

    const grandTotalValue = parseAmount(ledgerDetails?.accountDetails?.grandTotal);
    const dealerDiscountPercValue = parseAmount(ledgerDetails?.accountDetails?.dealerDiscountPerc);
    const additionalDealerDiscount = parseAmount(ledgerDetails?.accountDetails?.additionalDealerDiscount);
    const extraWorkOrderValue = parseAmount(ledgerDetails?.accountDetails?.extraWorkOrdersAMT);
    const collection = parseAmount(ledgerDetails?.accountDetails?.collection);
    const webappCollection = parseAmount(ledgerDetails?.accountDetails?.webappCollection);
    const write_off = parseAmount(ledgerDetails?.accountDetails?.write_off);
    const totalRefundAMT = parseAmount(ledgerDetails?.accountDetails?.totalRefundAMT);

    // tooltip - dealer price
    const dealerDiscountValue = roundTo2((grandTotalValue * dealerDiscountPercValue) / 100);
    const computedDealerPriceValue = roundTo2(grandTotalValue - dealerDiscountValue - additionalDealerDiscount + extraWorkOrderValue);

    // tooltip - webapp sales
    const webappSalesExcludingCreditNote = parseAmount(ledgerDetails?.accountDetails?.webappSalesExcludingCreditNote);
    const openingSales = parseAmount(ledgerDetails?.accountDetails?.openingSales);
    const creditNoteAMT = parseAmount(ledgerDetails?.accountDetails?.creditNoteAMT);
    const computedWebAppSales = roundTo2(webappSalesExcludingCreditNote - creditNoteAMT);

    // tooltip - due
    const computeDueAMT = roundTo2(computedDealerPriceValue - collection - webappCollection - write_off + totalRefundAMT);

    // tooltip - overdue
    const computeOverDueAMT = roundTo2(openingSales + webappSalesExcludingCreditNote - creditNoteAMT - collection - webappCollection - write_off + totalRefundAMT);

    const showTooltip = (tooltipId: LedgerTooltipKey): void => {
        if (!tooltipAccessMap[tooltipId]) {
            return;
        }
        setActiveTooltip(tooltipId);
    };

    const hideTooltip = (): void => {
        setActiveTooltip(null);
    };

    const getTooltipTriggerProps = (tooltipId: LedgerTooltipKey) => ({
        onMouseEnter: () => showTooltip(tooltipId),
        onMouseLeave: hideTooltip,
        onFocus: () => showTooltip(tooltipId),
        onBlur: hideTooltip,
    });

    const isTooltipDisabled = (tooltipId: LedgerTooltipKey): boolean => {
        if (!tooltipAccessMap[tooltipId]) {
            return true;
        }
        return activeTooltip !== null && activeTooltip !== tooltipId;
    };

    const canRenderTooltip = (tooltipId: LedgerTooltipKey): boolean => {
        return (
            ledgerDetails?.accountDetails?.officeType === OfficeTypeEnum.DEALEROFFICE
            && tooltipAccessMap[tooltipId]
        );
    };

    return (
        <>
            <Loader isLoading={isLoading} />

            {
                ledgerDetails && !IsNullOrUndefined(ledgerDetails) &&
                <div className="content-wrapper">
                    <div className="col-lg-12 mx-auto d-flex text-center">
                        <div className="auth-form-light text-left p-5 w-100">
                            <div className="brand-logo">
                                <img src="assets/images/logo.png" alt="logo" />
                            </div>
                            <h4 className='mt-3'>
                                {`Ledger Details of ${ledgerDetails.orderData.projectName}`}
                            </h4>
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="row">
                            <div className="col-lg-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-6 col-md-3">
                                                {
                                                    ledgerDetails.accountDetails.orderStatus &&
                                                    !IsStringNullEmptyOrUndefined(ledgerDetails.accountDetails.orderStatus) &&
                                                    <h6 className="fw-bold">
                                                        {statusBodyTemplate(ledgerDetails.accountDetails.orderStatus)}
                                                    </h6>
                                                }
                                                <h6 className="fw-bold">
                                                    {ledgerDetails.accountDetails.orderNumber}
                                                </h6>
                                                <h6 className="fw-bold mb-0">
                                                    {ledgerDetails.accountDetails.orderStatusDate}
                                                </h6>
                                            </div>

                                            <div className="col-6 col-md-3">
                                                <h6 className="fw-bold">
                                                    {ledgerDetails.ledgerDetails.salesPerson}
                                                </h6>
                                            </div>

                                            {
                                                ledgerDetails.accountDetails
                                                && !IsNullOrUndefined(ledgerDetails.accountDetails)
                                                && ledgerDetails.accountDetails.officeType
                                                && !IsStringNullEmptyOrUndefined(ledgerDetails.accountDetails.officeType)
                                                && ledgerDetails.accountDetails.officeType === OfficeTypeEnum.JBOFFICE
                                                &&
                                                <>
                                                    <div className="col-6 col-md-3">
                                                        <h6 className="m-sm-0 text-gray">
                                                            <span className="d-inline-flex align-items-center">
                                                                DUE
                                                                {
                                                                    canRenderTooltip('due') &&
                                                                    <i
                                                                        className="ms-2 bi bi-info-circle toolTipDue text-dark cursor-pointer"
                                                                        aria-label="Webapp sales formula info"
                                                                        {...getTooltipTriggerProps('due')}
                                                                    ></i>
                                                                }
                                                            </span>
                                                            {canRenderTooltip('due') && (
                                                                <LedgerFormulaTooltip
                                                                    target=".toolTipDue"
                                                                    title="Due Formula:"
                                                                    finalLine={<>Final Due = {computeDueAMT}</>}
                                                                    disabled={isTooltipDisabled('due')}
                                                                    onBeforeHide={hideTooltip}
                                                                >
                                                                    <div className="ledger-formula-tooltip-ui__row">
                                                                        <b>Due = </b>Dealer Price - Opening Balance - Webapp Collection - Write Off + Refunds
                                                                    </div>
                                                                    <div className="ledger-formula-tooltip-ui__row">
                                                                        <b>Due = </b>{computedDealerPriceValue} - {ledgerDetails.accountDetails.collection} - {ledgerDetails.accountDetails.webappCollection} - {ledgerDetails.accountDetails.write_off} + {ledgerDetails.accountDetails.totalRefundAMT}
                                                                    </div>
                                                                </LedgerFormulaTooltip>
                                                            )}
                                                        </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.accountDetails.due}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3">
                                                        <h6 className="m-sm-0 text-gray">
                                                            <span className="d-inline-flex align-items-center">
                                                                OVER DUE
                                                                {
                                                                    canRenderTooltip('overdue') &&
                                                                    <i
                                                                        className="ms-2 bi bi-info-circle toolTipOverDue text-dark cursor-pointer"
                                                                        aria-label="Webapp sales formula info"
                                                                        {...getTooltipTriggerProps('overdue')}
                                                                    ></i>
                                                                }
                                                            </span>
                                                            {canRenderTooltip('overdue') && (
                                                                <LedgerFormulaTooltip
                                                                    target=".toolTipOverDue"
                                                                    title="Overdue Formula:"
                                                                    finalLine={<>Final Overdue = {computeOverDueAMT}</>}
                                                                    disabled={isTooltipDisabled('overdue')}
                                                                    onBeforeHide={hideTooltip}
                                                                >
                                                                    <div className="ledger-formula-tooltip-ui__row">
                                                                        <b>Overdue = </b>Opening Sales + Webapp Sales - Credit Note - Opening Balance - Webapp Collection - Write Off + Refunds
                                                                    </div>
                                                                    <div className="ledger-formula-tooltip-ui__row">
                                                                        <b>Overdue = </b>{ledgerDetails.accountDetails.openingSales} + {webappSalesExcludingCreditNote} - {ledgerDetails.accountDetails.creditNoteAMT} - {ledgerDetails.accountDetails.collection} -{ledgerDetails.accountDetails.webappCollection} - {ledgerDetails.accountDetails.write_off} + {ledgerDetails.accountDetails.totalRefundAMT}
                                                                    </div>
                                                                </LedgerFormulaTooltip>
                                                            )}
                                                        </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.accountDetails.Overdue}
                                                        </h6>
                                                    </div>
                                                </>
                                            }

                                            {
                                                ledgerDetails.accountDetails
                                                && !IsNullOrUndefined(ledgerDetails.accountDetails)
                                                && ledgerDetails.accountDetails.officeType
                                                && !IsStringNullEmptyOrUndefined(ledgerDetails.accountDetails.officeType)
                                                && ledgerDetails.accountDetails.officeType === OfficeTypeEnum.DEALEROFFICE
                                                &&
                                                <div className="col-6 col-md-6">
                                                    <h6 className="m-sm-0 text-gray">
                                                        <span className="d-inline-flex align-items-center fw-bold" style={{ fontSize: 14 }}>
                                                            PROJECT CLOSING BALANCE
                                                            {
                                                                canRenderTooltip('projectClosingBalance') &&
                                                                <i
                                                                    className="ms-2 bi bi-info-circle toolTipProjectClosingBalance text-dark cursor-pointer"
                                                                    aria-label="Webapp sales formula info"
                                                                    {...getTooltipTriggerProps('projectClosingBalance')}
                                                                ></i>
                                                            }
                                                        </span>
                                                        {canRenderTooltip('projectClosingBalance') && (
                                                            <LedgerFormulaTooltip
                                                                target=".toolTipProjectClosingBalance"
                                                                title="Project Closing Balance Formula:"
                                                                finalLine={<>Project Closing Balance = {ledgerDetails.accountDetails.projectClosingBalance}</>}
                                                                disabled={isTooltipDisabled('projectClosingBalance')}
                                                                onBeforeHide={hideTooltip}
                                                            >
                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                    <b>Project Closing Balance = </b>Net Sales - Net Collection
                                                                </div>
                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                    <b>Project Closing Balance = </b>{ledgerDetails.accountDetails.netSales} - {ledgerDetails.accountDetails.netCollection}
                                                                </div>
                                                            </LedgerFormulaTooltip>
                                                        )}
                                                    </h6>
                                                    <span className="mt-2 fw-bold" style={{ fontSize: 14 }}>
                                                        {displayProjectClosingBalance(ledgerDetails.accountDetails.projectClosingBalance!)}
                                                    </span>
                                                </div>
                                            }

                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className='col-lg-12 col-md-12 mb-3'>
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row consign-block">
                                            {
                                                ledgerDetails && ledgerDetails.ledgerDetails && ledgerDetails.ledgerDetails.consigner &&
                                                <div className="col-md-6 col-12">
                                                    <h4 className="card-title">Consigner </h4>
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <p className='fw-bold'> {ledgerDetails && ledgerDetails.ledgerDetails.consigner && ledgerDetails.ledgerDetails.consigner.officeName} </p>
                                                        </div>
                                                        <div className="col-sm-12 col-md-4">
                                                            <span className='text-muted'> GST </span>
                                                            <p className='fw-bold'> {ledgerDetails && ledgerDetails.ledgerDetails.consigner && ledgerDetails.ledgerDetails.consigner.GSTNO} </p>
                                                        </div>
                                                        <div className="col-sm-12 col-md-8">
                                                            <span className='text-muted'> ADDRESS </span>
                                                            <p className='fw-bold'> {ledgerDetails && ledgerDetails.ledgerDetails.consigner && ledgerDetails.ledgerDetails.consigner.address} </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            }

                                            {
                                                ledgerDetails && ledgerDetails.ledgerDetails && ledgerDetails.ledgerDetails.consignee &&
                                                <div className="col-md-6 col-12">
                                                    <h4 className="card-title">Consignee </h4>
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <p className='fw-bold'> {ledgerDetails && ledgerDetails.ledgerDetails.consignee && ledgerDetails.ledgerDetails.consignee.officeName} </p>
                                                        </div>
                                                        <div className="col-sm-12 col-md-4">
                                                            <span className='text-muted'> GST </span>
                                                            <p className='fw-bold'> {ledgerDetails && ledgerDetails.ledgerDetails.consignee && ledgerDetails.ledgerDetails.consignee.GSTNO} </p>
                                                        </div>
                                                        <div className="col-sm-12 col-md-8">
                                                            <span className='text-muted'> ADDRESS </span>
                                                            <p className='fw-bold'> {ledgerDetails && ledgerDetails.ledgerDetails.consignee && ledgerDetails.ledgerDetails.consignee.address} </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                {/* if JB office */}
                                                {
                                                    ledgerDetails.accountDetails
                                                    && !IsNullOrUndefined(ledgerDetails.accountDetails)
                                                    && ledgerDetails.accountDetails.officeType
                                                    && !IsStringNullEmptyOrUndefined(ledgerDetails.accountDetails.officeType)
                                                    && ledgerDetails.accountDetails.officeType === OfficeTypeEnum.JBOFFICE
                                                    && (
                                                        <div className="row">
                                                            <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                <h6 className="m-sm-0 text-gray">GRAND TOTAL</h6>
                                                                <h6 className="mt-2 fw-bold">
                                                                    {ledgerDetails.accountDetails.grandTotal}
                                                                </h6>
                                                            </div>

                                                            {
                                                                Number(ledgerDetails.accountDetails.customerDiscount) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">ORDER DISCOUNT</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.discountPerc}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                ledgerDetails.accountDetails.discount !== "0" &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">DISCOUNT PRICE</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.discount}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                ledgerDetails.accountDetails.finalAmount !== "0" &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">ORDER FINAL AMOUNT</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.finalAmount}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                Number(ledgerDetails.accountDetails.write_off) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">WRITE OFF</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.write_off}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                Number(ledgerDetails.accountDetails.openingSales) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">OPENING SALES</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.openingSales}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                Number(ledgerDetails.accountDetails.webappSales) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">WEBAPP SALES</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.webappSales}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                Number(ledgerDetails.accountDetails.extraWorkOrdersAMT) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">EXTRA ORDER AMOUNT</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.extraWorkOrdersAMT}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                Number(ledgerDetails.accountDetails.invoiceCollection) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">INVOICE COLLECTION</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.invoiceCollection}
                                                                    </h6>
                                                                </div>
                                                            }

                                                            {
                                                                Number(ledgerDetails.accountDetails.PDC) > 0 &&
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">PDC COLLECTION</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.PDC}
                                                                    </h6>
                                                                </div>
                                                            }
                                                        </div>
                                                    )
                                                }

                                                {/* if Dealer Office */}
                                                {
                                                    ledgerDetails.accountDetails
                                                    && !IsNullOrUndefined(ledgerDetails.accountDetails)
                                                    && ledgerDetails.accountDetails.officeType
                                                    && !IsStringNullEmptyOrUndefined(ledgerDetails.accountDetails.officeType)
                                                    && ledgerDetails.accountDetails.officeType === OfficeTypeEnum.DEALEROFFICE
                                                    && (
                                                        <>
                                                            <div className="row">
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">GRAND TOTAL</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.grandTotal}
                                                                    </h6>
                                                                </div>

                                                                {
                                                                    Number(ledgerDetails.accountDetails.customerDiscount) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">ORDER DISCOUNT</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.discountPerc}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">ORDER FINAL AMOUNT</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.finalAmount}
                                                                    </h6>
                                                                </div>

                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">DEALER DISCOUNT</h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.displayDealerDiscount}
                                                                    </h6>
                                                                </div>

                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">
                                                                        <span className="d-inline-flex align-items-center">
                                                                            DEALER PRICE
                                                                            {canRenderTooltip('dealerPrice') && (
                                                                                <i
                                                                                    className="ms-2 bi bi-info-circle toolTipDealerPrice text-dark cursor-pointer"
                                                                                    aria-label="Dealer price formula info"
                                                                                    {...getTooltipTriggerProps('dealerPrice')}
                                                                                ></i>
                                                                            )}
                                                                        </span>
                                                                        {canRenderTooltip('dealerPrice') && (
                                                                            <LedgerFormulaTooltip
                                                                                target=".toolTipDealerPrice"
                                                                                title="Dealer Price Formula:"
                                                                                finalLine={<>Final Dealer Price = {formatAmount(computedDealerPriceValue)}</>}
                                                                                disabled={isTooltipDisabled('dealerPrice')}
                                                                                onBeforeHide={hideTooltip}
                                                                            >
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Dealer Discount = </b>Grand Total x (Dealer Disc. % / 100)
                                                                                </div>
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Dealer Discount = </b>{formatAmount(grandTotalValue)} x ({dealerDiscountPercValue} / 100) = {formatAmount(dealerDiscountValue)}
                                                                                </div>
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Dealer Price = </b>Grand Total - Dealer Discount - Addional Dealer Discount + Extra Work Order Amount
                                                                                </div>
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Dealer Price = </b>{formatAmount(grandTotalValue)} - {formatAmount(dealerDiscountValue)} - {ledgerDetails.accountDetails.additionalDealerDiscount} + {formatAmount(extraWorkOrderValue)}
                                                                                </div>
                                                                            </LedgerFormulaTooltip>
                                                                        )}
                                                                    </h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.dealerAmount}
                                                                    </h6>
                                                                </div>

                                                                {
                                                                    Number(ledgerDetails.accountDetails.additionalDealerDiscount) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">Additional Dealer Discount Value</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.additionalDealerDiscount}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    ledgerDetails.accountDetails.collection
                                                                    && !IsStringNullEmptyOrUndefined(ledgerDetails.accountDetails.collection)
                                                                    && ledgerDetails.accountDetails.collection !== "0" &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">OPENING BALANCE</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.collection}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.webappCollection) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">WEBAPP COLLECTION</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.webappCollection}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.openingSales) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">OPENING SALES</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.openingSales}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.webappSales) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">
                                                                            <span className="d-inline-flex align-items-center">
                                                                                WEBAPP SALES
                                                                                {/* {canRenderTooltip('webappSales') && (
                                                                                    <i
                                                                                        className="ms-2 bi bi-info-circle toolTipWebappSales text-dark cursor-pointer"
                                                                                        aria-label="Webapp sales formula info"
                                                                                        {...getTooltipTriggerProps('webappSales')}
                                                                                    ></i>
                                                                                )} */}
                                                                            </span>
                                                                            {canRenderTooltip('webappSales') && (
                                                                                <LedgerFormulaTooltip
                                                                                    target=".toolTipWebappSales"
                                                                                    title="Webapp Sales Formula:"
                                                                                    finalLine={<>Final Webapp sales = {formatAmount(computedWebAppSales)}</>}
                                                                                    disabled={isTooltipDisabled('webappSales')}
                                                                                    onBeforeHide={hideTooltip}
                                                                                >
                                                                                    <div className="ledger-formula-tooltip-ui__row">
                                                                                        <b>Webapp Sales = </b>Work Order Sales(incl. Extra & Error WO) - Credit Notes
                                                                                    </div>
                                                                                    <div className="ledger-formula-tooltip-ui__row">
                                                                                        <b>Webapp Sales = </b>{webappSalesExcludingCreditNote} - {creditNoteAMT}
                                                                                    </div>
                                                                                </LedgerFormulaTooltip>
                                                                            )}
                                                                        </h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.webappSales}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.extraWorkOrdersAMT) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">EXTRA ORDER AMOUNT</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.extraWorkOrdersAMT}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.creditNoteAMT) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">CREDIT NOTE AMOUNT</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.creditNoteAMT}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.write_off) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">WRITE OFF</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.write_off}
                                                                        </h6>
                                                                    </div>
                                                                }

                                                                {
                                                                    Number(ledgerDetails.accountDetails.totalRefundAMT) > 0 &&
                                                                    <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                        <h6 className="m-sm-0 text-gray">REFUNDS</h6>
                                                                        <h6 className="mt-2 fw-bold">
                                                                            {ledgerDetails.accountDetails.totalRefundAMT}
                                                                        </h6>
                                                                    </div>
                                                                }
                                                            </div>
                                                            <Divider />
                                                            <div className="row">
                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">
                                                                        <span className="d-inline-flex align-items-center">
                                                                            NET SALES
                                                                            {canRenderTooltip('netSales') && (
                                                                                <i
                                                                                    className="ms-2 bi bi-info-circle toolTipnetSales text-dark cursor-pointer"
                                                                                    aria-label="Net sales formula info"
                                                                                    {...getTooltipTriggerProps('netSales')}
                                                                                ></i>
                                                                            )}
                                                                        </span>
                                                                        {canRenderTooltip('netSales') && (
                                                                            <LedgerFormulaTooltip
                                                                                target=".toolTipnetSales"
                                                                                title="Net Sales Formula:"
                                                                                finalLine={<>Final Net Sales = {ledgerDetails.accountDetails.netSales}</>}
                                                                                disabled={isTooltipDisabled('netSales')}
                                                                                onBeforeHide={hideTooltip}
                                                                            >
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Net Sales = </b>Opening Sales + Webapp Sales – Credit Notes
                                                                                </div>
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Net Sales = </b>{ledgerDetails.accountDetails.openingSales} + {ledgerDetails.accountDetails.webappSales} - {ledgerDetails.accountDetails.creditNoteAMT}
                                                                                </div>
                                                                            </LedgerFormulaTooltip>
                                                                        )}
                                                                    </h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.netSales}
                                                                    </h6>
                                                                </div>

                                                                <div className="col-6 col-md-4 mb-3 mb-md-2">
                                                                    <h6 className="m-sm-0 text-gray">
                                                                        <span className="d-inline-flex align-items-center">
                                                                            NET COLLECTION
                                                                            {canRenderTooltip('netCollection') && (
                                                                                <i
                                                                                    className="ms-2 bi bi-info-circle toolTipnetCollection text-dark cursor-pointer"
                                                                                    aria-label="Net sales formula info"
                                                                                    {...getTooltipTriggerProps('netCollection')}
                                                                                ></i>
                                                                            )}
                                                                        </span>
                                                                        {canRenderTooltip('netCollection') && (
                                                                            <LedgerFormulaTooltip
                                                                                target=".toolTipnetCollection"
                                                                                title="Net Collection Formula:"
                                                                                finalLine={<>Final Net Collection = {ledgerDetails.accountDetails.netCollection}</>}
                                                                                disabled={isTooltipDisabled('netCollection')}
                                                                                onBeforeHide={hideTooltip}
                                                                            >
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Net Collection = </b>Opening Collection + Webapp Collection – Refunds
                                                                                </div>
                                                                                <div className="ledger-formula-tooltip-ui__row">
                                                                                    <b>Net Collection = </b>{ledgerDetails.accountDetails.collection} + {ledgerDetails.accountDetails.webappCollection} - {ledgerDetails.accountDetails.totalRefundAMT}
                                                                                </div>
                                                                            </LedgerFormulaTooltip>
                                                                        )}
                                                                    </h6>
                                                                    <h6 className="mt-2 fw-bold">
                                                                        {ledgerDetails.accountDetails.netCollection}
                                                                    </h6>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                <h6 className="m-sm-0 text-gray">ID REFERENCE</h6>
                                                <h6 className="mt-2 fw-bold">
                                                    {ledgerDetails.ledgerDetails.idReference} :- {ledgerDetails.ledgerDetails.idReferenceNumber}
                                                </h6>
                                            </div>

                                            <div className="col-md-4 col-12 mb-3 mb-md-2">
                                                <h6 className="m-sm-0 text-gray">BILLING NAME </h6>
                                                <h6 className="mt-2 fw-bold">
                                                    {ledgerDetails.ledgerDetails.billingName}
                                                </h6>
                                            </div>
                                            <div className="col-md-5 col-12 mb-3 mb-md-2">
                                                <h6 className="m-sm-0 text-gray">BILLING ADDRESS</h6>
                                                <h6 className="mt-2 fw-bold">
                                                    {ledgerDetails.ledgerDetails.billingAddress}
                                                </h6>
                                            </div>
                                            <div className="col-md-7 col-12 mb-3 mb-md-2">
                                                <h6 className="m-sm-0 text-gray">CUSTOMER PURCHASE ORDER NO</h6>
                                                <h6 className="mt-2 fw-bold">
                                                    {ledgerDetails.ledgerDetails.customerPurchaseOrderNo}
                                                </h6>
                                            </div>
                                            {
                                                ledgerDetails.ledgerDetails.purchaseOrderPdfUrl &&
                                                <div className="col-md-5 col-12 mb-3 mb-md-2">
                                                    <h6 className="m-sm-0 text-gray">CUSTOMER PURCHASE ORDER PDF</h6>
                                                    <h6 className="mt-2 fw-bold">
                                                        <a href={ledgerDetails.ledgerDetails.purchaseOrderPdfUrl}>Click here for view pdf</a>
                                                    </h6>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <h5 className='fw-bold mb-3'> Project Address </h5>
                                                <div className="row">
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">ADDRESS LINE 1 </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.addressLine1! : ""}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">ADDRESS LINE 2 </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.addressLine2! : ""}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">PIN CODE </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.pinCode! : ''}
                                                        </h6>
                                                    </div>

                                                    {/* <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">ADDRESS LINE 3 </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.addressLine3! : ''}
                                                        </h6>
                                                    </div> */}

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">CITY </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.city! : ''}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">STATE </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.state! : ''}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">COUNTRY</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.country! : ""}
                                                        </h6>
                                                    </div>



                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">LANDMARK </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.delivaryAddress ? ledgerDetails.ledgerDetails.delivaryAddress.landmark! : ""}
                                                        </h6>
                                                    </div>

                                                    {
                                                        ledgerDetails.ledgerDetails.delivaryAddress &&
                                                        !IsNullOrUndefined(ledgerDetails.ledgerDetails.delivaryAddress) &&
                                                        ledgerDetails.ledgerDetails.delivaryAddress.googlePinLink &&
                                                        !IsStringNullEmptyOrUndefined(ledgerDetails.ledgerDetails.delivaryAddress.googlePinLink) &&
                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">GOOGLE PIN LINK </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                <a href={ledgerDetails.ledgerDetails.delivaryAddress.googlePinLink}>
                                                                    {ledgerDetails.ledgerDetails.delivaryAddress.googlePinLink}
                                                                </a>
                                                            </h6>
                                                        </div>
                                                    }

                                                    {
                                                        ledgerDetails.ledgerDetails.delivaryAddress &&
                                                        !IsNullOrUndefined(ledgerDetails.ledgerDetails.delivaryAddress) &&
                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">PROJECT ADDRESS</h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.delivaryAddressStatus &&
                                                                    (ledgerDetails.ledgerDetails.delivaryAddressStatus === DelivaryAddressStatusEnum.DEALEROFFICE ?
                                                                        ledgerDetails.ledgerDetails.consignee ?
                                                                            ledgerDetails.ledgerDetails.consignee.officeName : ledgerDetails.ledgerDetails.consigner?.officeName : `${ledgerDetails.ledgerDetails.delivaryAddressStatus.toLocaleUpperCase()} ADDRESS`)}
                                                            </h6>
                                                        </div>
                                                    }

                                                    {
                                                        ledgerDetails.ledgerDetails.goDownAddress &&
                                                        !IsNullOrUndefined(ledgerDetails.ledgerDetails.goDownAddress) &&
                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">Godown Address</h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.goDownAddress}
                                                            </h6>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {ledgerDetails.ledgerDetails.selectedDelivaryAddressStatus &&
                                !IsNullOrUndefined(ledgerDetails.ledgerDetails.selectedDelivaryAddressStatus) &&
                                <div className="col-md-12 col-md-12 mb-3">
                                    <div className="card card-rounded">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-12">
                                                    <h5 className='fw-bold mb-3'> Delivery Address </h5>
                                                    <div className="row">
                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">DELIVERY LOCATION </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressStatus}
                                                            </h6>
                                                        </div>
                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">ADDRESS LINE 1 </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails ? ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails.addressLine1! : ""}
                                                            </h6>
                                                        </div>

                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">ADDRESS LINE 2 </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails ? ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails.addressLine2! : ""}
                                                            </h6>
                                                        </div>

                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">PIN CODE </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails ? ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails.pincode! : ''}
                                                            </h6>
                                                        </div>

                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">CITY </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails ? ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails.cityName : ''}
                                                            </h6>
                                                        </div>

                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">STATE </h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails ? ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails.stateName! : ''}
                                                            </h6>
                                                        </div>

                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">COUNTRY</h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails ? ledgerDetails.ledgerDetails.selectedDelivaryAddressDetails.countryName! : ""}
                                                            </h6>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            <div className="col-md-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <h4 className='fw-bold mb-3'> Contact Details </h4>
                                                <div className="row">
                                                    <h6 className='mb-2 fw-bold'>Direct Client</h6>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">NAME</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {
                                                                ledgerDetails.ledgerDetails.contactDetails &&
                                                                    ledgerDetails.ledgerDetails.contactDetails.directClient &&
                                                                    ledgerDetails.ledgerDetails.contactDetails.directClient.salutation
                                                                    ? ledgerDetails.ledgerDetails.contactDetails.directClient.salutation : ''
                                                            }
                                                            {
                                                                ledgerDetails.ledgerDetails.contactDetails &&
                                                                    ledgerDetails.ledgerDetails.contactDetails.directClient &&
                                                                    ledgerDetails.ledgerDetails.contactDetails.directClient.name
                                                                    ? ledgerDetails.ledgerDetails.contactDetails.directClient.name : ''
                                                            }
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">MOBILE NUMBER</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.contactDetails &&
                                                                ledgerDetails.ledgerDetails.contactDetails.directClient &&
                                                                ledgerDetails.ledgerDetails.contactDetails.directClient.mobile
                                                                ? ledgerDetails.ledgerDetails.contactDetails.directClient.mobile : ''}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">EMAIL ADDRESS</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.contactDetails &&
                                                                ledgerDetails.ledgerDetails.contactDetails.directClient &&
                                                                ledgerDetails.ledgerDetails.contactDetails.directClient.emailAddress
                                                                ? ledgerDetails.ledgerDetails.contactDetails.directClient.emailAddress : ''}
                                                        </h6>
                                                    </div>
                                                </div>

                                                <div className="row mt-3">
                                                    <h6 className='mb-2 fw-bold'>Site Person</h6>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">NAME</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.contactDetails &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson.salutation
                                                                ? ledgerDetails.ledgerDetails.contactDetails.sitePerson.salutation : ''}
                                                            {ledgerDetails.ledgerDetails.contactDetails &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson.name
                                                                ? ledgerDetails.ledgerDetails.contactDetails.sitePerson.name : ''}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">MOBILE NUMBER</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.contactDetails &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson.mobile
                                                                ? ledgerDetails.ledgerDetails.contactDetails.sitePerson.mobile : ''}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">EMAIL ADDRESS</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.contactDetails &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson &&
                                                                ledgerDetails.ledgerDetails.contactDetails.sitePerson.emailAddress
                                                                ? ledgerDetails.ledgerDetails.contactDetails.sitePerson.emailAddress : ''}
                                                        </h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <h4 className='fw-bold mb-3'>Architect Firm Details [{ledgerDetails.ledgerDetails.isAchitectFrimVerify ? "Verified" : "Non Verified"}] </h4>
                                                <div className="row">
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">COMPANY NAME</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.companyName}
                                                        </h6>
                                                    </div>
                                                    {/* <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">WEBSITE</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                        {ledgerDetails.ledgerDetails.consultantDetails.}
                                                        </h6>
                                                    </div> */}

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">ADDRESS LINE 1 </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.addressLine1}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">ADDRESS LINE 2 </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.addressLine2}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">PIN CODE </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.pinCode}
                                                        </h6>
                                                    </div>

                                                    {/* <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">ADDRESS LINE 3 </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.addressLine3}
                                                        </h6>   
                                                    </div> */}
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">CITY </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.city}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">STATE </h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.state}
                                                        </h6>
                                                    </div>

                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">COUNTRY</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.consultantDetails.countryName}
                                                        </h6>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <h4 className='fw-bold mb-3'>Architect details [{ledgerDetails.ledgerDetails.isArchitectDetailVerify ? "Verified" : "Non Verified"}] </h4>
                                                <div className="row">
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">NAME</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.principleArchitect.salutation} {ledgerDetails.ledgerDetails.principleArchitect.firstName} {ledgerDetails.ledgerDetails.principleArchitect.lastName}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">MOBILE NUMBER</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.principleArchitect.mobile}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">EMAIL ADDRESS</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.principleArchitect.emailAddress}
                                                        </h6>
                                                    </div>
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">USER TYPE</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.principleArchitect.userType}
                                                        </h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-md-12 col-md-12 mb-3">
                                <div className="card card-rounded">
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <div className="row">
                                                    <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                        <h6 className="m-sm-0 text-gray">PRO LEVEL STATUS</h6>
                                                        <h6 className="mt-2 fw-bold">
                                                            {ledgerDetails.ledgerDetails.proLevel}
                                                        </h6>
                                                    </div>
                                                    {
                                                        ledgerDetails.ledgerDetails.proLevel === 'PROPLUS' &&
                                                        <div className="col-6 col-md-3 mb-3 mb-md-2">
                                                            <h6 className="m-sm-0 text-gray">PRO PLUS PERCENTAGE</h6>
                                                            <h6 className="mt-2 fw-bold">
                                                                {ledgerDetails.ledgerDetails.proPlusPercent}
                                                            </h6>
                                                        </div>
                                                    }

                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div >
            }
        </>
    )
}
